import { CircleLookup } from 'particles/lookups/CircleLookup';
import { IconLookup } from 'particles/lookups/IconLookup';

/**
 * Flattened path lookup tables for the path kernel (stage 4).
 *
 * The reference ParticlePath walks sceneProps.lookups[type].table — an array
 * of {x, y, scale, ...} objects (built once per lookup class, e.g.
 * CircleLookup's 360 unit-circle steps, IconLookup's ~1000-point logo
 * outline). The kernel reads the same values from Float32Array lanes instead:
 * the object tables are flattened ONCE, lazily on first use, into parallel
 * x / y / scale arrays indexed by the same integer path index. Values are
 * copied verbatim from the REAL lookup classes (imported read-only) so any
 * future table tweak stays single-sourced.
 *
 * Only the lookups the ported behaviours use exist here so far: CIRCLE
 * (icon + intro orbit paths) and ICON (the spaces/systems iconPath slot —
 * the slot ships in stage 4, its departments arrive later). SINE / ICON2 /
 * TRI / RECT flatten the same way when a department needs them.
 *
 * The lookupId lane on each path slot indexes this module's table array.
 */

export const LOOKUP_CIRCLE = 0;
export const LOOKUP_ICON = 1;

export interface FlatPathLookup {
	readonly length: number;
	readonly x: Float32Array;
	readonly y: Float32Array;
	/** table[i].scale — only read by the scale-from-path branch (PATH_FLAG_SCALE_ACTIVE). */
	readonly scale: Float32Array;
}

let cache: ReadonlyArray<FlatPathLookup> = null;

function flatten(table: Array<{ x: number; y: number; scale?: number }>): FlatPathLookup {
	const length = table.length;
	const x = new Float32Array(length);
	const y = new Float32Array(length);
	const scale = new Float32Array(length);

	for (let i = 0; i < length; i++) {
		x[i] = table[i].x;
		y[i] = table[i].y;
		scale[i] = table[i].scale || 0;
	}

	return { length: length, x: x, y: y, scale: scale };
}

/** The flattened tables, indexed by LOOKUP_*. Built once, on first call. */
export function getPathLookups(): ReadonlyArray<FlatPathLookup> {
	if (!cache) {
		//Index order must match the LOOKUP_* constants:
		cache = [flatten(new CircleLookup().table), flatten(new IconLookup().table)];
	}

	return cache;
}
